// src/pages/documents/FieldMappingCard.tsx

import React, { useEffect, useState } from 'react';
import { Card, Select, Button, Tag, Alert, message } from 'antd';
import { CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PdfFieldMapping, MAPPABLE_ENTITIES, groupedMappableEntities } from '../../types';
import { isLegacyAnswerSource, mappingFieldOptions } from './mappingFieldOptions';
import EntityRowSelector from './EntityRowSelector';
import { probateDocsService } from './probatedocs.service';

const { Option } = Select;

// Styled Components
const StyledCard = styled(Card)<{ $isMapped: boolean }>`
  border: 1px solid ${props => props.$isMapped ? '#10B981' : '#F59E0B'};
  border-radius: 8px;
  transition: box-shadow 0.2s, border-color 0.2s;
  background: ${props => props.$isMapped 
    ? 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)' 
    : 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)'};
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .ant-card-head-title { white-space: normal; }
  .ant-card-head {
    border-bottom: 1px solid ${props => props.$isMapped ? '#D1FAE5' : '#FDE68A'};
  }
`;

const FieldInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const FieldName = styled.span`
  font-weight: 500;
  font-family: monospace;
  overflow-wrap: anywhere;
  font-size: 14px;
`;

const MappedIndicator = styled(CheckCircleOutlined)`
  color: #10B981;
  font-size: 18px;
`;

const ContextSection = styled.div`
  margin-bottom: 16px;
  
  label {
    font-size: 12px;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 4px;
  }
`;

const SurroundingText = styled.p`
  font-style: italic;
  color: #4B5563;
  background: #F9FAFB;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  margin: 4px 0 0 0;
`;

const MappingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  label {
    font-size: 12px;
    color: #374151;
    font-weight: 500;
  }
`;

const DefaultValue = styled.div`
  margin-top: 12px;
  padding: 8px;
  background: #F3F4F6;
  border-radius: 4px;

  label {
    font-size: 12px;
    color: #6B7280;
    font-weight: 500;
  }

  span {
    font-size: 13px;
    color: #374151;
    margin-left: 8px;
  }
`;

const TemplatePreview = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 4px;

  label {
    font-size: 12px;
    color: #1D4ED8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 4px;
  }

  code {
    font-size: 13px;
    color: #1E3A8A;
    word-break: break-all;
  }

  p {
    font-size: 12px;
    color: #6B7280;
    margin: 6px 0 0 0;
  }
`;

interface FieldMappingCardProps {
  field: PdfFieldMapping;
  probateDocId: string;
  documentFields?: PdfFieldMapping[];
  onUpdate: () => void;
}

const FieldMappingCard: React.FC<FieldMappingCardProps> = ({ 
  field, 
  documentFields = [field],
  probateDocId,
  onUpdate 
}) => {
  // Parse current entity and field from databaseFieldName, e.g.
  // "user.firstName" or "owner.addresses[0].city".
  //
  // Everything after the first dot is the field path, matching how the backend
  // resolves it. Taking only the second segment loses the remainder of a
  // nested path, so a saved address mapping would reopen showing nothing
  // selected and silently reset on the next save.
  const parseMapping = () => {
    if (field.databaseFieldName && field.relatedEntity) {
      const parts = field.databaseFieldName.split('.');
      return {
        entity: field.relatedEntity,
        field: parts.length > 1 ? parts.slice(1).join('.') : ''
      };
    }
    return { entity: '', field: '' };
  };

  const initialMapping = parseMapping();
  const [selectedEntity, setSelectedEntity] = useState<string>(initialMapping.entity);
  const [selectedField, setSelectedField] = useState<string>(initialMapping.field);
  // Which row, for entities an estate holds several of. "[0]" unless chosen.
  const [entitySelector, setEntitySelector] = useState<string>(field.entitySelector || '');

  useEffect(() => {
    const mapping = parseMapping();
    setSelectedEntity(mapping.entity);
    setSelectedField(mapping.field);
    setEntitySelector(field.entitySelector || '');
  }, [field.databaseFieldName, field.relatedEntity, field.entitySelector]);

  const entityDef = selectedEntity ? MAPPABLE_ENTITIES[selectedEntity] : undefined;
  const [saving, setSaving] = useState(false);

  const handleEntityChange = (entity: string) => {
    setSelectedEntity(entity);
    setSelectedField(''); // Reset field when entity changes
    setEntitySelector('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fieldId = String(field.id);
      await probateDocsService.updateFieldMapping(probateDocId, fieldId, {
        relatedEntity: selectedEntity || null,
        databaseFieldName: selectedEntity && selectedField
          ? `${selectedEntity}.${selectedField}`
          : null,
        // Only meaningful for entities an estate holds several of.
        entitySelector: entityDef?.multiple ? entitySelector || null : null,
        // Saving a single mapping here replaces any concatenation template
        // built in the visual mapper; otherwise the old template would keep
        // winning at fill time and this edit would silently have no effect.
        template: null,
      });
      message.success(
        field.template
          ? 'Mapping saved. The existing concatenation template was cleared.'
          : 'Mapping saved successfully'
      );
      onUpdate();
    } catch (error: any) {
      console.error('Failed to save mapping:', error);
      message.error(error.response?.data?.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const isMapped = field.databaseFieldName !== null || !!field.template;
  const hasChanges = selectedEntity !== initialMapping.entity ||
                     selectedField !== initialMapping.field ||
                     entitySelector !== (field.entitySelector || '');

  const getFieldTypeColor = (type: string) => {
    switch (type) {
      case 'TextField':
        return 'blue';
      case 'CheckBox':
        return 'green';
      case 'RadioButton':
        return 'orange';
      case 'ComboBox':
        return 'purple';
      default:
        return 'default';
    }
  };

  const cardTitle = (
    <FieldInfo>
      <Tag color={getFieldTypeColor(field.type)}>{field.type}</Tag>
      <FieldName>{field.pdfFieldName}</FieldName>
      {isMapped && <MappedIndicator />}
    </FieldInfo>
  );

  return (
    <StyledCard 
      $isMapped={isMapped}
      title={cardTitle}
      size="small"
    >
      {/* Surrounding Text Context */}
      {field.surroundingText && (
        <ContextSection>
          <label>Context (Nearby Text):</label>
          <SurroundingText>"{field.surroundingText}"</SurroundingText>
        </ContextSection>
      )}

      {isLegacyAnswerSource(selectedEntity) && <Alert type="warning" showIcon
        message="Legacy answer mapping requires source review"
        description="Choose the estate record, person, family relationship or calculation that owns this fact. A saved PDF answer key does not establish an accessible input or a verified source."
        style={{ marginBottom: 12 }} />}
      {/* Entity Selector */}
      <MappingSection>
        <FormGroup>
          <label>Map to Entity</label>
          <Select
            aria-label={`Source entity for ${field.pdfFieldName}, page ${field.pageNumber || 1}`}
            value={selectedEntity || undefined}
            onChange={handleEntityChange}
            placeholder="Select entity..."
            allowClear
            style={{ width: '100%' }}
          >
            {groupedMappableEntities(initialMapping.entity).map(([group, members]) => (
              <Select.OptGroup key={group} label={group}>
                {members.map(([key, entity]) => (
                  <Option key={key} value={key}>{entity.label}</Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
        </FormGroup>

        {/* Which row, for entities an estate holds several of. Naming the
            entity and the field is only half an answer while an estate has
            four assets. */}
        {entityDef?.multiple && (
          <FormGroup>
            <label>Which one?</label>
            <EntityRowSelector
              entity={entityDef}
              entityKey={selectedEntity}
              value={entitySelector}
              onChange={setEntitySelector}
              resetKey={`${field.id}:${selectedEntity}`}
            />
          </FormGroup>
        )}

        {/* Field Selector (shown when entity is selected) */}
        {selectedEntity && MAPPABLE_ENTITIES[selectedEntity] && (
          <FormGroup>
            <label>Select Field</label>
            <Select
              aria-label={`Source field for ${field.pdfFieldName}, page ${field.pageNumber || 1}`}
              value={selectedField || undefined}
              onChange={setSelectedField}
              showSearch
              optionFilterProp="children"
              placeholder="Select field..."
              allowClear
              style={{ width: '100%' }}
            >
              {mappingFieldOptions(selectedEntity, documentFields, selectedField).map((f) => (
                <Option key={f.value} value={f.value}>{f.label}</Option>
              ))}
            </Select>
          </FormGroup>
        )}
      </MappingSection>

      {/* Concatenation Template (from the visual mapper). Read-only here —
          it supersedes databaseFieldName at fill time, so surface it and
          warn that saving a single mapping below will clear it. */}
      {field.template && (
        <TemplatePreview>
          <label>Concatenation Template (wins at fill time)</label>
          <code>{field.template}</code>
          <p>Saving a mapping below will replace this template.</p>
        </TemplatePreview>
      )}

      {/* Default Value */}
      {field.defaultValue && (
        <DefaultValue>
          <label>Default Value:</label>
          <span>{field.defaultValue}</span>
        </DefaultValue>
      )}

      {/* Save Button */}
      <div style={{ marginTop: 16 }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges && !selectedEntity}
          block
        >
          {isMapped ? 'Update Mapping' : 'Save Mapping'}
        </Button>
      </div>
    </StyledCard>
  );
};

export default FieldMappingCard;